import './ReceptionForm.scss';

import { ExportOutlined } from '@ant-design/icons';
import { DateField } from '@refinedev/antd';
import { Alert, Card, Flex, Form, message, Modal, Tag } from 'antd';
import { FormProps } from 'antd/lib';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import PackageItemsList from '@/components/logistic/PackageItems/PackageItemsList';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { ADMIN_URL, COMPANY_URLS } from '@/constants/urls';
import { COUNTRY, useCountryContext } from '@/contexts/CountryContext';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { RECEPTION_TYPE } from '@/model/Logistic/Reception/ReceptionType';

import ReceptionContactForm from './ReceptionContactForm';
import ReceptionDetailForm from './ReceptionDetailForm';
import ReceptionLogisticForm from './ReceptionLogisticForm';
import ReceptionReturnAddressForm from './ReceptionReturnAddressForm';
import { useReceptionForm } from './useReceptionForm';

interface ReceptionFormProps {
  formProps: FormProps;
  edit?: boolean;
  packageItems: PackageItem[];
  setPackageItems: (items: PackageItem[]) => void;
  setSaveAddress?: (save: boolean) => void;
}

const ReceptionForm = (props: ReceptionFormProps) => {
  const { isBrasilSelected } = useCountryContext();
  const { t } = useTranslation();
  const { formProps, edit, packageItems, setPackageItems, setSaveAddress } = props;
  const { initialValues } = formProps;
  const [sp] = useSearchParams();
  const ticketId = sp.get('ticket') ? Number(sp.get('ticket')) : undefined;
  const initialValuesModified = useMemo(
    () =>
      initialValues
        ? {
            ...initialValues,
            company: initialValues?.company?.id,
            shipper: initialValues?.shipper?.id,
            receiver_localidad: initialValues?.receiver_localidad?.id,
            receiver_provincia: initialValues?.receiver_localidad?.provincia?.id,
            receiver_pais: initialValues?.receiver_localidad?.provincia?.pais?.id,
            ticket: edit ? initialValues?.ticket : ticketId ? ticketId : initialValues?.ticket?.id,
          }
        : ticketId
          ? {
              ticket: ticketId,
              receiver_pais: isBrasilSelected ? COUNTRY.BRASIL : COUNTRY.ARGENTINA,
            }
          : { receiver_pais: isBrasilSelected ? COUNTRY.BRASIL : COUNTRY.ARGENTINA },
    [edit, initialValues, isBrasilSelected, ticketId],
  );
  const {
    initialValuesSetted,
    companySelectProps,
    shippersSelectProps,
    ticketsSelectProps,
    movilLink,
    // contactos para envio de presupuesto
    companyContactData,
    loadingCompanyContactData,
    sendToClient,
    setSendToClient,
  } = useReceptionForm(formProps, initialValuesModified, edit);

  return (
    <Form
      {...formProps}
      onFinish={values => {
        const companyWarn = formProps.form?.getFieldWarning('company') || [];

        if (companyWarn?.length > 0) {
          Modal.error({
            title: t('Faltan datos de contacto'),
            content: (
              <div className="ShipmentFormWarnModal">
                {t('Faltan datos de contacto')}. {t('Por favor, revise')}
                <ul>
                  {companyWarn?.length > 0 && (
                    <li>
                      <a
                        className="ShipmentFormWarnLink"
                        href={`${ADMIN_URL}${COMPANY_URLS.ADMIN_EMPRESA}/${values.company}`}
                        target="_blank"
                        rel="noopener noreferrer">
                        {t('Empresa o Técnico que enviará el paquete')}{' '}
                        <ExportOutlined className="ShipmentFormWarnLinkIcon" />
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            ),
            okText: t('Aceptar'),
            maskClosable: true,
          });

          return;
        } else {
          if (formProps?.onFinish) {
            formProps.onFinish({ ...values, status: PACKAGE_STATES.PENDIENTE });
          }
        }
      }}
      layout="vertical"
      className="ReceptionForm"
      initialValues={initialValuesModified}
      onFinishFailed={errorInfo => {
        if (errorInfo.errorFields.length > 0) {
          message.error(t('Por favor, revisa los campos requeridos antes de enviar el formulario'));
        }
      }}>
      {edit && (
        <div className="ReceptionFormRow">
          <div>
            <div className="ReceptionFormDataId">
              {t('Id de recepción')}: {initialValues?.id}
            </div>
            <Flex>
              {initialValues?.status != undefined && (
                <PackageStateTag packageStatus={initialValues?.status} />
              )}
              {initialValues?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
                <Tag color="geekblue">{t('Automática')}</Tag>
              )}
            </Flex>
          </div>
          <div>
            <div>
              <b>{t('Fecha de creación')}: </b>
              <DateField value={initialValues?.created_at} />
            </div>
            <div>
              <b>{t('Fecha última modificación')}: </b>
              <DateField value={initialValues?.updated_at} />
            </div>
          </div>
        </div>
      )}

      <Card className="ReceptionFormSection">
        <Form.Item
          label={<b>{t('Ticket')}</b>}
          name={'ticket'}
          rules={[
            {
              required: true,
            },
          ]}>
          <AntdSelect {...ticketsSelectProps} allowClear showSearch />
        </Form.Item>
      </Card>
      <Card className="ReceptionFormSection">
        <div className="ReceptionFormTitle">{t('Detalles de la recepción')}</div>
        <ReceptionDetailForm
          formProps={formProps}
          companySelectProps={companySelectProps}
          shippersSelectProps={shippersSelectProps}
        />
      </Card>
      {(!edit ||
        (initialValues?.id &&
          initialValues?.tipo != RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE)) && (
        <>
          <Card className="ReceptionFormSection">
            <div className="ReceptionFormTitle">{t('Envío de presupuesto')}</div>
            <ReceptionContactForm
              formProps={formProps}
              companyContactData={companyContactData}
              loadingCompanyContactData={loadingCompanyContactData}
              sendToClient={sendToClient}
              setSendToClient={setSendToClient}
            />
          </Card>
          <Card className="ReceptionFormSection">
            <div className="ReceptionFormTitle">{t('Dirección de devolución')}</div>
            <ReceptionReturnAddressForm
              formProps={formProps}
              initialValuesModified={initialValuesModified}
              shippersSelectProps={shippersSelectProps}
              setSaveAddress={setSaveAddress}
              initialValuesSetted={initialValuesSetted}
            />
          </Card>
        </>
      )}
      {edit && (
        <Card className="ReceptionFormSection">
          <div className="ReceptionFormTitle">{t('Logística')}</div>
          <ReceptionLogisticForm />
        </Card>
      )}

      <div className="ReceptionFormTitle">{t('Elementos')}</div>
      <PackageItemsList
        packageItems={packageItems}
        setPackageItems={setPackageItems}
        package={{ id: initialValues?.id, movil_link: movilLink }}
        editMode={
          initialValues?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE
            ? 'partial'
            : 'enabled'
        }
      />
      {initialValues?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
        <Alert
          style={{ marginTop: 16 }}
          showIcon
          type="info"
          message={t(
            'No se pueden modificar los elementos de una recepción creada automáticamente',
          )}
        />
      )}
    </Form>
  );
};

export default ReceptionForm;
